import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "./tokens";

/* Comparison matrix. The recommended column is Eva + CoBrain,
   engineer-deployed; DIY and Generic AI bracket the alternatives. */

type Cell = string | { v: string; tone?: "yes" | "no" | "partial" };
type Row = { label: string; cells: Cell[] };

const COLS = [
  { key: "diy",   name: "DIY stack",        sub: "Notion + SOPs",      tone: "neutral" as const },
  { key: "ai",    name: "Generic AI",       sub: "ChatGPT / Claude",   tone: "neutral" as const },
  { key: "both",  name: "Eva + CoBrain",    sub: "the upgrade path",   tone: "best" as const },
];

const ROWS: Row[] = [
  { label: "Who it serves",
    cells: ["Whole team manually", "Whoever prompts it", "Owner + whole company"] },
  { label: "Shared context across people",
    cells: [{v: "Partial", tone: "partial"}, {v: "—", tone: "no"}, {v: "✓", tone: "yes"}] },
  { label: "Plugs into every employee's email & CRM",
    cells: [{v: "—", tone: "no"}, {v: "—", tone: "no"}, {v: "✓", tone: "yes"}] },
  { label: "Multi-agent with permissions",
    cells: [{v: "—", tone: "no"}, {v: "—", tone: "no"}, {v: "✓", tone: "yes"}] },
  { label: "Real-time org telemetry (deals, team, revenue)",
    cells: [{v: "—", tone: "no"}, {v: "—", tone: "no"}, {v: "✓", tone: "yes"}] },
  { label: "Owner-level superpowers",
    cells: [{v: "—", tone: "no"}, {v: "Partial", tone: "partial"}, {v: "✓", tone: "yes"}] },
  { label: "Owner approval gates built-in",
    cells: [{v: "If you remember", tone: "partial"}, {v: "DIY rules", tone: "partial"}, {v: "✓", tone: "yes"}] },
  { label: "Setup",
    cells: ["Weeks of doc-writing", "Days of glue work", "Live in days, engineer-deployed"] },
  { label: "Starting price",
    cells: ["Your evenings", "$20/seat", "from $1,500/mo, engineer included"] },
];

const cellStyle = (col: typeof COLS[number], isHeader = false) => {
  const base: React.CSSProperties = {
    padding: "14px 16px", fontSize: 14, textAlign: "left",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.78)",
    verticalAlign: "top",
  };
  if (col.tone === "best") {
    base.background = "rgba(196,130,58,0.16)";
    base.color = "#fff";
    base.fontWeight = 600;
  }
  if (isHeader) {
    base.padding = "20px 16px";
    base.borderBottom = "1px solid rgba(255,255,255,0.18)";
  }
  return base;
};

const renderCell = (c: Cell) => {
  if (typeof c === "string") return <span>{c}</span>;
  const color = c.tone === "yes" ? PALETTE.amberSoft
    : c.tone === "no" ? "rgba(255,255,255,0.32)"
    : c.tone === "partial" ? "rgba(255,255,255,0.7)"
    : "rgba(255,255,255,0.85)";
  return <span style={{ color, fontWeight: c.tone === "yes" ? 700 : 500 }}>{c.v}</span>;
};

export const CompareMatrix = () => (
  <>
    <style>{`
      @media (max-width: 760px) {
        .cb-compare-table { display: none !important; }
      }
      @media (min-width: 761px) {
        .cb-compare-mobile { display: none !important; }
      }
    `}</style>

    {/* DESKTOP / TABLET — full matrix */}
    <div className="cb-compare-table" style={{
      background: "rgba(255,255,255,0.04)",
      borderRadius: 20, padding: 8,
      border: "1px solid rgba(255,255,255,0.08)",
      overflowX: "auto",
    }}>
      <table style={{ width: "100%", minWidth: 720, borderCollapse: "separate", borderSpacing: 0 }}>
      <thead>
        <tr>
          <th style={{
            padding: "20px 16px", textAlign: "left",
            fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em",
            textTransform: "uppercase", color: "rgba(255,255,255,0.45)", fontWeight: 700,
            width: "22%", borderBottom: "1px solid rgba(255,255,255,0.18)",
          }}>Capability</th>
          {COLS.map((c) => (
            <th key={c.key} style={cellStyle(c, true)}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{
                  fontFamily: FONT_DISPLAY, fontStyle: c.tone === "best" ? "italic" : "normal",
                  fontSize: c.tone === "best" ? 22 : 18, fontWeight: 400,
                  color: c.tone === "best" ? PALETTE.amberSoft
                    : "rgba(255,255,255,0.78)",
                  lineHeight: 1,
                }}>{c.name}</span>
                <span style={{
                  fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: c.tone === "best" ? PALETTE.amberSoft : "rgba(255,255,255,0.45)",
                  fontWeight: 700,
                }}>{c.sub}</span>
                {c.tone === "best" && (
                  <span style={{
                    marginTop: 4, alignSelf: "flex-start",
                    padding: "2px 8px", borderRadius: 999,
                    background: "rgba(196,130,58,0.28)",
                    border: "1px solid rgba(196,130,58,0.5)",
                    fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "0.14em",
                    textTransform: "uppercase", color: "#fff", fontWeight: 700,
                  }}>Best fit · growing teams</span>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {ROWS.map((row) => (
          <tr key={row.label}>
            <td style={{
              padding: "14px 16px", fontFamily: FONT_BODY,
              fontSize: 13.5, color: "rgba(255,255,255,0.55)", fontWeight: 600,
              borderBottom: "1px solid rgba(255,255,255,0.08)", verticalAlign: "top",
            }}>{row.label}</td>
            {row.cells.map((c, i) => (
              <td key={i} style={cellStyle(COLS[i])}>{renderCell(c)}</td>
            ))}
          </tr>
        ))}
      </tbody>
      </table>
    </div>

    {/* MOBILE — stacked highlight + competitor accordion */}
    <CompareMobile />
  </>
);

/* ---------- Mobile variant ---------- */
const CompareMobile = () => {
  const best = COLS[2]; // Eva + CoBrain
  return (
    <div className="cb-compare-mobile" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Highlight card — Eva + CoBrain */}
      <div style={{
        background: "rgba(196,130,58,0.12)",
        border: "1px solid rgba(196,130,58,0.42)",
        borderRadius: 20, padding: 22,
        display: "flex", flexDirection: "column", gap: 12,
      }}>
        <span style={{
          alignSelf: "flex-start",
          padding: "3px 10px", borderRadius: 999,
          background: "rgba(196,130,58,0.32)",
          fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "0.14em",
          textTransform: "uppercase", color: "#fff", fontWeight: 700,
        }}>Best fit · growing teams</span>
        <div>
          <div style={{
            fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 28,
            color: PALETTE.amberSoft, fontWeight: 400, lineHeight: 1,
          }}>{best.name}</div>
          <div style={{
            marginTop: 6,
            fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.12em",
            textTransform: "uppercase", color: PALETTE.amberSoft, fontWeight: 700,
          }}>{best.sub}</div>
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {ROWS.map((r) => {
            const v = r.cells[2];
            const text = typeof v === "string" ? v : v.v;
            return (
              <li key={r.label} style={{
                display: "grid", gridTemplateColumns: "auto 1fr", gap: 10, alignItems: "baseline",
                fontSize: 13.5, color: "rgba(255,255,255,0.92)",
              }}>
                <span style={{ color: PALETTE.amberSoft, fontSize: 14, lineHeight: 1, fontWeight: 700 }}>✓</span>
                <span><span style={{ color: "rgba(255,255,255,0.55)", fontWeight: 500 }}>{r.label}:</span> {text}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Competitor accordion-style cards */}
      <div style={{
        fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.14em",
        textTransform: "uppercase", color: "rgba(255,255,255,0.45)", fontWeight: 700,
        marginTop: 4,
      }}>The other paths</div>
      {COLS.slice(0, 2).map((col, colIdx) => (
        <details key={col.key} style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 14, padding: "12px 16px",
        }}>
          <summary style={{
            cursor: "pointer", listStyle: "none",
            display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12,
          }}>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{
                fontFamily: FONT_DISPLAY,
                fontStyle: "normal",
                fontSize: 18, color: "#fff", fontWeight: 400, lineHeight: 1,
              }}>{col.name}</span>
              <span style={{
                fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "0.12em",
                textTransform: "uppercase", color: "rgba(255,255,255,0.45)", fontWeight: 700,
              }}>{col.sub}</span>
            </span>
            <span aria-hidden style={{ color: PALETTE.amberSoft, fontSize: 18, lineHeight: 1 }}>+</span>
          </summary>
          <ul style={{ listStyle: "none", padding: "12px 0 0", margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {ROWS.map((r) => {
              const v = r.cells[colIdx];
              const text = typeof v === "string" ? v : v.v;
              const tone = typeof v === "string" ? undefined : v.tone;
              const color = tone === "yes" ? PALETTE.amberSoft
                : tone === "no" ? "rgba(255,255,255,0.32)"
                : tone === "partial" ? "rgba(255,255,255,0.6)"
                : "rgba(255,255,255,0.78)";
              return (
                <li key={r.label} style={{ fontSize: 13, color: "rgba(255,255,255,0.78)" }}>
                  <span style={{ color: "rgba(255,255,255,0.45)", fontWeight: 500 }}>{r.label}:</span>{" "}
                  <span style={{ color }}>{text}</span>
                </li>
              );
            })}
          </ul>
        </details>
      ))}
    </div>
  );
};